import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models (F921 / F9211)
// ============================================================

export interface F921 {
  f921_01: string;          // 單號 (PK)
  f921_02: string;          // 員工代號
  f921_10: string;          // 代理人代號
  f921_11: string | null;   // 申請日期
  f921_12: string;          // 假別代號
  f921_13: string | null;   // 開始日期
  f921_14: string;          // 開始時間
  f921_15: string | null;   // 結束日期
  f921_16: string;          // 結束時間
  f921_17: number;          // 請假總數(計算)
  f921_18: string;          // 假別單位(日/時)
  f921_19: number;          // 請假總數(彙總)
  f921_20: number;          // 可用餘額
  f921_21: string;          // 請假事由
  f921_22: string;          // 初核人員
  f921_23: string | null;   // 初核日期
  f921_24: string;          // 初核備註
  f921_25: string;          // 複核人員
  f921_26: string | null;   // 複核日期
  f921_27: string;          // 備註
  f921_28: string;          // 備註(memo)
  f921_29: string;          // 審核層級碼
  f921_30: string;          // 狀態碼
  f921_31: string;          // 部門代號
  f921_87: string;          // 修改人員
  f921_88: string | null;   // 修改日期
  f921_91: string;          // 入單人員
  f921_92: string | null;   // 入單日期
  f921_93: string;          // 建立人員
  f921_72: string;          // 審核人
  f921_73: string | null;   // 審核日期
  f921_67: string;          // 人事查核人
  f921_68: string | null;   // 人事查核日期
  f921_69: string;          // 人事查核備註
  f921_74: string;          // 審核備註
  f921_79: string;          // 附件備註

  // Associations
  details?: F9211[];
}

export interface F9211 {
  f9211_01: string;          // 請假單號 (FK)
  f9211_02: string;          // 員工代號
  f9211_03: string;          // 月份 (YYYYMM)
  f9211_11: string;          // 假別代號
  f9211_12: number;          // 該月請假數
  f9211_13: number;          // 該月請假數(備用)
  f9211_14: string;          // 假別單位
  f9211_91: string;          // 入單人員
  f9211_92: string | null;   // 入單日期
  f9211_93: string;          // 建立人員
}

// ============================================================
// Request / Response types
// ============================================================

export interface P922AListResponse {
  data: F921[];
  total: number;
  page: number;
  size: number;
}

export interface P922ACreateRequest {
  master: Partial<F921>;
  details: Partial<F9211>[];
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

export const p922aApi = {
  async list(page = 1, size = 20): Promise<P922AListResponse> {
    const res = await apiClient.get('/api/p922a', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<F921> {
    const res = await apiClient.get(`/api/p922a/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: P922ACreateRequest): Promise<F921> {
    const res = await apiClient.post('/api/p922a', data);
    return res.data;
  },

  async update(id: string, data: P922ACreateRequest): Promise<F921> {
    const res = await apiClient.put(`/api/p922a/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/p922a/${encodeURIComponent(id)}`);
  },

  async lookupName(type: string, code: string): Promise<string> {
    if (!code || !code.trim()) return '';
    try {
      const res = await apiClient.get(`/api/p922a/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`);
      return res.data.name || '';
    } catch {
      return '';
    }
  },

  /** Get leave type info (name + unit) from F908 */
  async getLeaveTypeInfo(code: string): Promise<{ code: string; name: string; unit: string }> {
    if (!code || !code.trim()) return { code: '', name: '', unit: '' };
    try {
      const res = await apiClient.get(`/api/p922a/leave-type-info/${encodeURIComponent(code.trim())}`);
      return res.data;
    } catch {
      return { code, name: '', unit: '' };
    }
  },

  /** Get employee leave balance from F9131 */
  async getLeaveBalance(employeeCode: string): Promise<LeaveBalance[]> {
    if (!employeeCode || !employeeCode.trim()) return [];
    try {
      const res = await apiClient.get(`/api/p922a/leave-balance/${encodeURIComponent(employeeCode.trim())}`);
      return res.data.data || [];
    } catch {
      return [];
    }
  },

  /** Get proxy employee list from F905 */
  async getProxyList(employeeCode: string): Promise<ProxyEmployee[]> {
    if (!employeeCode || !employeeCode.trim()) return [];
    try {
      const res = await apiClient.get(`/api/p922a/proxy-list/${encodeURIComponent(employeeCode.trim())}`);
      return res.data.data || [];
    } catch {
      return [];
    }
  },
};

// Additional types for new endpoints
export interface LeaveBalance {
  f9131_01: string;  // employee code
  f9131_02: string;  // year
  f9131_03: string;  // leave type code
  f9131_11: number;  // available balance
  f9131_14: number;  // used/approved count
}

export interface ProxyEmployee {
  code: string;
  name: string;
}
